import { useCallback, useEffect, useMemo, useRef } from 'react';

import { attemptsApi } from '../api/attempts';

/**
 * Heartbeat cadence. Sent even when nothing happened, so a trainer's board can
 * tell "sitting here stuck" apart from "closed the tab and left" — the first is
 * the student who most needs someone to walk over.
 */
const FLUSH_INTERVAL_MS = 20_000;

/**
 * After this long without an edit, run or paste, we stop counting active time.
 *
 * Without it, a tab left open over lunch reads as ninety minutes of work, and
 * `active_secs` — which gates whether we say anything about an attempt at all —
 * becomes a measure of how long a laptop was on.
 */
const IDLE_TIMEOUT_MS = 120_000;

interface Buffer {
  activeSecs: number;
  typedChars: number;
  pastes: number[];
  runs: number;
  submits: number;
}

const emptyBuffer = (): Buffer => ({
  activeSecs: 0,
  typedChars: 0,
  pastes: [],
  runs: 0,
  submits: 0,
});

export interface SolveTelemetry {
  recordTyped: (chars: number) => void;
  recordPaste: (chars: number) => void;
  recordRun: () => void;
  recordSubmit: () => void;
  /** Ask the server to confirm a solve. It checks; it does not take our word. */
  recordSolved: () => void;
}

export interface UseSolveTelemetryOptions {
  slug: string;
  language: string;
  /** Current code, read only at flush time to report its length. */
  getCodeChars: () => number;
  /**
   * Off for trainer preview — a trainer trying a problem as a student must not
   * accumulate solve evidence against their own name, and must not appear on
   * the lab board as a student who pasted a solution.
   */
  enabled?: boolean;
}

/**
 * Records how the student is working, and reports it in intervals.
 *
 * Everything accumulates in refs rather than state: this runs on every
 * keystroke, and re-rendering the editor to count a character would be a
 * performance bug wearing a telemetry costume.
 */
export function useSolveTelemetry({
  slug,
  language,
  getCodeChars,
  enabled = true,
}: UseSolveTelemetryOptions): SolveTelemetry {
  const buffer = useRef<Buffer>(emptyBuffer());
  const lastInteraction = useRef<number>(0);
  const solvedClaim = useRef(false);

  // Read at flush time so the interval below never needs re-registering when
  // the student switches language or the code changes.
  const latest = useRef({ slug, language, getCodeChars, enabled });
  latest.current = { slug, language, getCodeChars, enabled };

  const markInteraction = useCallback(() => {
    lastInteraction.current = Date.now();
  }, []);

  const flush = useCallback(async () => {
    const { slug, language, getCodeChars, enabled } = latest.current;
    if (!enabled || !slug) return;

    const pending = buffer.current;
    const claimSolved = solvedClaim.current;
    const nothingHappened =
      pending.activeSecs === 0 &&
      pending.typedChars === 0 &&
      pending.pastes.length === 0 &&
      pending.runs === 0 &&
      pending.submits === 0;

    // An idle student is still a student in the room, so an empty heartbeat is
    // worth sending — but not one that also has no solve to confirm and no
    // code to measure, which is just a page nobody has touched.
    if (nothingHappened && !claimSolved && getCodeChars() === 0) return;

    // Reset BEFORE awaiting. A keystroke landing mid-request would otherwise be
    // wiped by a reset that ran after it.
    buffer.current = emptyBuffer();
    solvedClaim.current = false;

    try {
      await attemptsApi.heartbeat({
        problem_slug: slug,
        language,
        active_secs: pending.activeSecs,
        typed_chars: pending.typedChars,
        pastes: pending.pastes,
        runs: pending.runs,
        submits: pending.submits,
        code_chars: getCodeChars(),
        solved: claimSolved,
      });
    } catch {
      // Deliberately silent, and deliberately not retried.
      //
      // This is background bookkeeping on a college network. A student mid-way
      // through a problem must never see an error about it, and a queue of
      // retries would turn a brief outage into a burst of duplicated intervals
      // that inflate the very counters a trainer reads as evidence. Losing one
      // interval is the cheaper failure.
    }
  }, []);

  // Active-time ticker. One second is coarse enough to be free and fine enough
  // that a two-hour lab's totals are meaningful.
  useEffect(() => {
    if (!enabled) return;
    const tick = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInteraction.current > IDLE_TIMEOUT_MS) return;
      buffer.current.activeSecs += 1;
    }, 1_000);
    return () => window.clearInterval(tick);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void flush();
    }, FLUSH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, flush]);

  // Flush what we have when the student leaves the problem or hides the tab.
  //
  // No `sendBeacon`: it cannot carry the Authorization header this API needs.
  // `fetch(..., {keepalive})` could, but at the cost of duplicating the auth
  // interceptor here. Since a heartbeat goes out every twenty seconds anyway,
  // an unload that loses its final request costs under one interval — not worth
  // a second, divergent request path to recover.
  useEffect(() => {
    if (!enabled) return;
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      void flush();
    };
  }, [enabled, flush]);

  const recordTyped = useCallback(
    (chars: number) => {
      if (chars <= 0) return;
      buffer.current.typedChars += chars;
      markInteraction();
    },
    [markInteraction],
  );

  const recordPaste = useCallback(
    (chars: number) => {
      if (chars <= 0) return;
      buffer.current.pastes.push(chars);
      markInteraction();
    },
    [markInteraction],
  );

  // Runs and submits flush immediately rather than waiting for the interval:
  // they are the moments a trainer watching the board wants to see land, and
  // they are also when the student is most likely to walk away.
  const recordRun = useCallback(() => {
    buffer.current.runs += 1;
    markInteraction();
    void flush();
  }, [flush, markInteraction]);

  const recordSubmit = useCallback(() => {
    buffer.current.submits += 1;
    markInteraction();
    void flush();
  }, [flush, markInteraction]);

  const recordSolved = useCallback(() => {
    solvedClaim.current = true;
    markInteraction();
    void flush();
  }, [flush, markInteraction]);

  // Memoised: the workspace feeds these into `useCallback` deps and into an
  // effect that registers global keyboard shortcuts. A fresh object each render
  // would re-register those listeners on every keystroke.
  return useMemo(
    () => ({ recordTyped, recordPaste, recordRun, recordSubmit, recordSolved }),
    [recordTyped, recordPaste, recordRun, recordSubmit, recordSolved],
  );
}
